/**
 * MemberChainView
 * Level 1 drill-in: shows all members of a saga as a vertical chain.
 * Each member row shows type label, status, and progress.
 * Clicking a row drills into MemberDetailView (level 2).
 */

import { realUnitNumber } from '@resida/shared';
import { ArrowLeft, CheckCircle2, ArrowRight, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { SAGA_FALLBACK_LABELS, type MemberWorkflowSummary, type SagaWorkflowSummary } from '../../types/workflow';

const MEMBER_TYPE_LABELS: Record<string, string> = {
  lease_setup: 'Lease Setup',
  payment_setup: 'Payment Setup',
  move_in_walkthrough: 'Move-In Walkthrough',
  move_out_walkthrough: 'Move-Out Walkthrough',
};

function MemberStatusIcon({ status }: { status: MemberWorkflowSummary['status'] }) {
  if (status === 'completed') {
    return <CheckCircle2 className="h-4 w-4 text-success shrink-0" aria-label="Complete" />;
  }
  if (status === 'active') {
    return <ArrowRight className="h-4 w-4 text-primary shrink-0" aria-label="In progress" />;
  }
  return <Lock className="h-4 w-4 text-muted-foreground shrink-0" aria-label="Upcoming" />;
}

interface MemberChainViewProps {
  saga: SagaWorkflowSummary;
  onBack: () => void;
  onSelectMember: (member: MemberWorkflowSummary) => void;
}

export function MemberChainView({ saga, onBack, onSelectMember }: MemberChainViewProps) {
  const unitLabel = realUnitNumber(saga.unitLabel);
  const fallbackLabel = SAGA_FALLBACK_LABELS[saga.type];

  return (
    <div data-testid="workflow-member-chain" className="flex flex-col gap-3 p-4">
      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        className="self-start -ml-2"
        data-testid="workflow-member-chain-back-btn"
        aria-label="Back to workflow list"
      >
        <ArrowLeft className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
        Back
      </Button>

      {/* Header — append the unit only when the saga actually has a unit label (unitLabel != null),
          so a unit without a number reads e.g. "Move-in" rather than "Move-in — Move-in". Title
          stem is saga-type-aware (issue #1027) so lease_end never reads as "Move-in". */}
      <div>
        <h3 className="text-sm font-semibold text-foreground">
          {unitLabel ? `${fallbackLabel} — ${unitLabel}` : fallbackLabel}
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          {saga.members.filter((m) => m.status === 'completed').length} / {saga.members.length} steps complete
        </p>
      </div>

      {/* Member chain */}
      <div className="flex flex-col gap-2" role="list" aria-label={`${fallbackLabel} steps`}>
        {saga.members.map((member, i) => {
          const position = member.position ?? i + 1;
          const label = MEMBER_TYPE_LABELS[member.type] ?? member.type;
          const unsatisfiedCount = member.preconditions.filter((p) => !p.satisfied).length;

          return (
            <button
              key={member.id ?? `member-${i}`}
              type="button"
              role="listitem"
              data-testid={`workflow-member-row-${position}`}
              onClick={() => onSelectMember(member)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors hover:bg-accent/50 cursor-pointer ${
                member.status === 'active'
                  ? 'border-primary/30 bg-primary/5'
                  : member.status === 'completed'
                  ? 'border-success/20 bg-success/5'
                  : 'border-border bg-background'
              }`}
              aria-label={`${label} — ${member.status}`}
            >
              {/* Position badge */}
              <span
                className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold shrink-0 ${
                  member.status === 'completed'
                    ? 'bg-success/20 text-success'
                    : member.status === 'active'
                    ? 'bg-primary/20 text-primary'
                    : 'bg-muted text-muted-foreground'
                }`}
                aria-hidden="true"
              >
                {position}
              </span>

              {/* Label + progress */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {label}
                  {member.stepLabel && member.status === 'active' && (
                    <span className="text-muted-foreground font-normal"> — {member.stepLabel}</span>
                  )}
                </p>
                {member.currentStep !== null && member.status === 'active' && (
                  <p className="text-xs text-muted-foreground">
                    Step {member.currentStep} of {member.totalSteps}
                    {unsatisfiedCount > 0 && ` · ${unsatisfiedCount} item${unsatisfiedCount !== 1 ? 's' : ''} needed`}
                  </p>
                )}
              </div>

              {/* Status icon */}
              <MemberStatusIcon status={member.status} />
            </button>
          );
        })}
      </div>
    </div>
  );
}
